import { apiRequest } from "./client";
import type { Role } from "./auth";

export type Admin = {
  id: number;
  username: string;
  email: string | null;
  full_name: string | null;
  role: Role;
  must_change_password: boolean;
  is_active: boolean;
  created_at: string;
  created_by: number | null;
  last_login: string | null;
  // null = all tabs (incl. future); [] = no tabs; list = restricted set.
  // Ignored for super_admin (backend role check overrides).
  allowed_tab_ids: string[] | null;
};

export type AdminListResponse = { items: Admin[] };
export type AdminCreateIn = {
  username: string;
  role: Role;
  email?: string;
  full_name?: string;
};
export type AdminCreateOut = { admin: Admin; temp_password: string };
export type TempPasswordOut = { temp_password: string | null };
export type AdminUpdateIn = {
  email?: string | null;
  full_name?: string | null;
  role?: Role;
  // Only send this key when the caller means to change the admin's tab access.
  // null = grant all tabs; [] = revoke all tabs.
  allowed_tab_ids?: string[] | null;
};

export function listAdmins() {
  return apiRequest<AdminListResponse>("/admin/users");
}

export function createAdmin(body: AdminCreateIn) {
  return apiRequest<AdminCreateOut>("/admin/users", { method: "POST", body });
}

export function getTempPassword(id: number) {
  return apiRequest<TempPasswordOut>(`/admin/users/${id}/temp-password`);
}

export function regeneratePassword(id: number) {
  return apiRequest<TempPasswordOut>(`/admin/users/${id}/regenerate-password`, {
    method: "POST",
  });
}

export function deactivateAdmin(id: number) {
  return apiRequest<{ ok: boolean }>(`/admin/users/${id}/deactivate`, {
    method: "POST",
  });
}

export function updateAdmin(id: number, body: AdminUpdateIn) {
  return apiRequest<Admin>(`/admin/users/${id}`, {
    method: "PATCH",
    body,
  });
}

export function deleteAdmin(id: number) {
  return apiRequest<{ ok: boolean }>(`/admin/users/${id}`, {
    method: "DELETE",
  });
}

export function changePassword(current_password: string, new_password: string) {
  return apiRequest<{ ok: boolean }>("/admin/users/change-password", {
    method: "POST",
    body: { current_password, new_password },
  });
}

export type LogoutReason = "manual" | "inactivity" | "heartbeat_timeout" | "superseded";

export type SessionLogItem = {
  id: number;
  login_at: string;
  logout_at: string | null;
  logout_reason: LogoutReason | null;
  duration_seconds: number;
  ip_address: string | null;
  user_agent: string | null;
};

export type UserDailyLogsResponse = {
  admin_id: number;
  date: string;
  total_seconds: number;
  first_login: string | null;
  last_logout: string | null;
  sessions: SessionLogItem[];
};

export function getUserLogs(id: number, date: string) {
  return apiRequest<UserDailyLogsResponse>(`/admin/users/${id}/logs`, {
    query: { date },
  });
}

export type UserTotalTimeResponse = {
  admin_id: number;
  total_seconds: number;
  session_count: number;
  active_days: number;
  first_login: string | null;
  last_activity: string | null;
};

export function getUserTotalTime(id: number) {
  return apiRequest<UserTotalTimeResponse>(`/admin/users/${id}/total-time`);
}
